/** Facility Preview/Confirm payloads & responses (camelCase JSON; parse tolerates PascalCase). */

export interface PtSessionBookingRequest {
  scheduleId: number;
  trainerId: number;
  memberIds: number[];
  startDate: string;
  startTime: string;
  type: number;
  duration: number;
  isFreeAssessment?: boolean;
  isRecurring: boolean;
  repeatInterval: number;
  repeatFrequency: number;
  repeatByDay: string;
  isRepeatForever: boolean;
  recurrenceEndType: number;
  recurrenceOccurrences: number | null;
  recurrenceEndDate: string | null;
}

export interface OfferedSessionType {
  isGroup: boolean;
  isAssessment: boolean;
  duration: number;
  label: string;
  code: string;
}

export function offeredSessionTypeCode(isAssessment: boolean, isGroup: boolean, duration: number): string {
  return `${isAssessment ? 'a' : isGroup ? 'g' : 'p'}-${duration}`;
}

export function parseOfferedSessionTypes(data: unknown): OfferedSessionType[] {
  const root = data as Record<string, unknown> | null;
  const raw = root?.['types'] ?? root?.['Types'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: OfferedSessionType[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const duration = Number(row['durationMinutes'] ?? row['DurationMinutes']);
    if (!Number.isFinite(duration) || duration <= 0) {
      continue;
    }
    const isGroup = row['isGroup'] === true || row['IsGroup'] === true;
    const isAssessment = row['isAssessment'] === true || row['IsAssessment'] === true;
    const label = String(row['label'] ?? row['Label'] ?? '');
    out.push({
      isGroup,
      isAssessment,
      duration,
      label: label || (isAssessment ? 'Fitness Assessment' : `${isGroup ? 'Group' : 'Private'} (${duration} min)`),
      code: offeredSessionTypeCode(isAssessment, isGroup, duration)
    });
  }
  return out;
}

export interface PtSessionBookingOccurrencePreview {
  startDate: Date | null;
  startTime: string | null;
  endTime: string | null;
}

export interface TrainingSessionBookingMemberPreview {
  memberId: number;
  currencyId: number;
  balanceMinutes: number;
  eligibleTotalBalanceMinutes: number;
  requiredTotalMinutes: number;
  hasSufficientBalance: boolean;
}

export interface TrainingSessionBookingPreviewResponse {
  isValid: boolean;
  errors: string[];
  occurrenceCount: number;
  occurrences: PtSessionBookingOccurrencePreview[];
  attendees: TrainingSessionBookingMemberPreview[];
}

export interface PublicTrainerRow {
  Id: number;
  State: number;
  Person?: { FirstName?: string; LastName?: string; FullName?: string };
}

function parseIntLoose(v: unknown): number {
  if (v == null) {
    return 0;
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return Math.trunc(v);
  }
  if (typeof v === 'string' && v.length > 0) {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function parseBoolLoose(v: unknown): boolean {
  if (typeof v === 'boolean') {
    return v;
  }
  if (typeof v === 'number') {
    return v !== 0;
  }
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    return s === 'true' || s === '1';
  }
  return false;
}

function stringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(e => (e != null ? String(e) : '')).filter(s => s.length > 0);
}

function parseDateLoose(v: unknown): Date | null {
  if (v == null) {
    return null;
  }
  if (v instanceof Date) {
    return v;
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function stringifyTime(v: unknown): string | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'string') {
    return v;
  }
  if (typeof v === 'object' && v !== null) {
    const m = v as Record<string, unknown>;
    const h = parseIntLoose(m['hours'] ?? m['Hours'] ?? m['hour'] ?? m['Hour']);
    const min = parseIntLoose(m['minutes'] ?? m['Minutes'] ?? m['minute'] ?? m['Minute']);
    const s = parseIntLoose(m['seconds'] ?? m['Seconds'] ?? m['second'] ?? m['Second']);
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return String(v);
}

function occurrenceList(raw: unknown): PtSessionBookingOccurrencePreview[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: PtSessionBookingOccurrencePreview[] = [];
  for (const e of raw) {
    if (typeof e !== 'object' || e === null) {
      continue;
    }
    const m = e as Record<string, unknown>;
    out.push({
      startDate: parseDateLoose(m['startDate'] ?? m['StartDate']),
      startTime: stringifyTime(m['startTime'] ?? m['StartTime']),
      endTime: stringifyTime(m['endTime'] ?? m['EndTime'])
    });
  }
  return out;
}

function attendeePreviewList(raw: unknown): TrainingSessionBookingMemberPreview[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: TrainingSessionBookingMemberPreview[] = [];
  for (const e of raw) {
    if (typeof e !== 'object' || e === null) {
      continue;
    }
    const m = e as Record<string, unknown>;
    out.push({
      memberId: parseIntLoose(m['memberId'] ?? m['MemberId']),
      currencyId: parseIntLoose(m['currencyId'] ?? m['CurrencyId']),
      balanceMinutes: parseIntLoose(m['balanceMinutes'] ?? m['BalanceMinutes']),
      eligibleTotalBalanceMinutes: parseIntLoose(
        m['eligibleTotalBalanceMinutes'] ?? m['EligibleTotalBalanceMinutes']
      ),
      requiredTotalMinutes: parseIntLoose(m['requiredTotalMinutes'] ?? m['RequiredTotalMinutes']),
      hasSufficientBalance: parseBoolLoose(m['hasSufficientBalance'] ?? m['HasSufficientBalance'])
    });
  }
  return out;
}

/** Parse Preview/Confirm error body (same shape as preview). */
export function parseTrainingGroupBookingPreview(data: unknown): TrainingSessionBookingPreviewResponse | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const m = data as Record<string, unknown>;
  try {
    return {
      isValid: parseBoolLoose(m['isValid'] ?? m['IsValid']),
      errors: stringList(m['errors'] ?? m['Errors']),
      occurrenceCount: parseIntLoose(m['occurrenceCount'] ?? m['OccurrenceCount']),
      occurrences: occurrenceList(m['occurrences'] ?? m['Occurrences']),
      attendees: attendeePreviewList(m['attendees'] ?? m['Attendees'] ?? m['members'] ?? m['Members'])
    };
  } catch {
    return null;
  }
}

export const REPEAT_BY_DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

/** Same order as df-frontdesk `PtSessionRepeatByDay.dayLabels` (Sun…Sat). */
export const REPEAT_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function repeatByDayToApiString(days: boolean[]): string {
  const parts: string[] = [];
  for (let i = 0; i < 7 && i < days.length; i++) {
    if (days[i]) {
      parts.push(REPEAT_BY_DAY_CODES[i]);
    }
  }
  return parts.join(',');
}

/**
 * Nested on `GET Training/Booking/{id}` when the booking is a group PT session.
 * Matches facility `TrainingGroupSessionDetailResponse` / member DTOs (PascalCase JSON).
 */
export interface TrainingSessionMemberApiRow {
  BookingId?: number;
  MemberId?: number;
  MemberFirstName?: string;
  MemberLastName?: string;
  MemberFullName?: string;
  PTSessions?: number;
  GroupSessions?: number;
  CurrencyBalances?: unknown[];
}

export interface FacilityBookingSessionPayload {
  IsGroupSession?: boolean;
  ScheduleId?: number;
  TrainerId?: number;
  Members?: TrainingSessionMemberApiRow[];
}
